import React from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import AuthProviderWithNavigate from 'auth/AuthProviderWithNavigate';
import { SENTRY_DSN } from 'helpers/constants';
import { setupStore, setGlobalStore } from 'reduxState';
import { isInvalidRefreshTokenError, clearAuth0Cache } from 'helpers/authErrors';
import App from './App';
import './index.css';

if (import.meta.env.NODE_ENV !== 'development') {
  Sentry.init({ dsn: SENTRY_DSN, environment: import.meta.env.NODE_ENV });
}

const container = document.getElementById('root');
const root = createRoot(container!);

const store = setupStore();
setGlobalStore(store);

// Preemptively clear Auth0 cache if it might be stale
// This prevents Auth0 SDK from trying to use invalid refresh tokens during initialization
const clearStaleAuth0Cache = () => {
  const auth0Keys = Object.keys(localStorage).filter(key => key.startsWith('@@auth0spajs@@'));

  if (auth0Keys.length > 0) {
    // Check if we're on a fresh page load after potential logout
    // If there's Auth0 cache but no active session indicators, clear it
    console.log('Found Auth0 cache on startup, will let SDK validate it');

    // Add a global error handler for unhandled promise rejections from Auth0
    const handleAuthError = (event: PromiseRejectionEvent) => {
      if (isInvalidRefreshTokenError(event.reason)) {
        console.error('Detected invalid refresh token during Auth0 initialization, clearing cache');

        // Clear all Auth0 cache using shared utility
        clearAuth0Cache();

        // Reload the page to start fresh
        event.preventDefault(); // Prevent the error from being logged
        window.location.reload();
      }
    };

    window.addEventListener('unhandledrejection', handleAuthError);

    // Clean up after 5 seconds (by then Auth0 should have initialized)
    setTimeout(() => {
      window.removeEventListener('unhandledrejection', handleAuthError);
    }, 5000);
  }
};

clearStaleAuth0Cache();

root.render(
  <Provider store={store}>
    <Router>
      <AuthProviderWithNavigate>
        <Routes>
          <Route path="*" element={<App />} />
        </Routes>
      </AuthProviderWithNavigate>
    </Router>
  </Provider>,
);
